import { LocaleTypeEnum } from "@/types/localization";
import Cookies from "js-cookie";
import { config } from "@/config";
import { getRefreshToken } from "./getRefreshToken";
import { getLangFromRefreshToken } from "./getLangFromRefreshToken";
import { detectBrowserLanguage } from "./detectBrowserLanguage";
import { normalizeToSupportedLang } from "./normalizeToSupportedLang";

const getLangFromUrl = (): string | null =>
  new URL(location.href).searchParams.get("lang");

const getLangFromJwt = (): string | null => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    return getLangFromRefreshToken(refreshToken) ?? null;
  } catch {
    // Malformed/undecodable token: skip this signal and continue the chain.
    return null;
  }
};

const getLangFromCookie = (): string | null => Cookies.get("lang") ?? null;

/**
 * Single source of truth for the active UI locale.
 *
 * Resolution chain (each signal validated against the supported set; a
 * present-but-invalid signal is skipped, not failed):
 *
 *   1. URL `?lang=`        request-scoped explicit override (not persisted)
 *   2. JWT `lang` claim    durable stored preference
 *   3. `lang` cookie       remembered prior choice
 *   4. browser language    inferred from `navigator.languages`
 *   5. DEFAULT_LANG        global fallback (English)
 */
export const getLanguage = (): LocaleTypeEnum => {
  return (
    normalizeToSupportedLang(getLangFromUrl()) ??
    normalizeToSupportedLang(getLangFromJwt()) ??
    normalizeToSupportedLang(getLangFromCookie()) ??
    detectBrowserLanguage() ??
    config.constants.DEFAULT_LANG
  );
};
